import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, Trash2, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";

export const CartSheet = () => {
  const { items, removeItem, subtotal, count } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="open-cart-button"
          className="relative flex items-center gap-2 rounded-full bg-cocoa px-5 py-2.5 text-white transition-transform duration-300 hover:-translate-y-0.5"
        >
          <ShoppingBag className="h-4 w-4" />
          <span className="text-sm font-medium">Cart</span>
          {count > 0 && (
            <span
              data-testid="cart-count-badge"
              className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-babyblue text-xs font-bold text-cocoadark"
            >
              {count}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col bg-cream sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl text-ink">Your Box</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center text-center text-ashydark">
            <ShoppingBag className="mb-4 h-12 w-12 opacity-40" />
            <p>Your box is empty.</p>
            <p className="text-sm">Add some sweet treats!</p>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-3 overflow-y-auto py-4">
              {items.map((item) => (
                <div
                  key={item.lineId}
                  data-testid="cart-line-item"
                  className="rounded-xl border border-ashy/40 bg-white p-4"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-ink">{item.label}</p>
                      {item.flavors && item.flavors.length > 0 && (
                        <p className="mt-1 text-sm text-ashydark">
                          {item.flavors.map((f) => f.name).join(", ")}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="font-semibold text-cocoa">${item.price.toFixed(2)}</span>
                      <button
                        data-testid="remove-cart-item"
                        onClick={() => removeItem(item.lineId)}
                        className="text-ashydark transition-colors hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-ashy/40 pt-4">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-ashydark">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-heading text-2xl text-ink">
                  ${subtotal.toFixed(2)}
                </span>
              </div>
              <Button
                data-testid="go-to-checkout-button"
                onClick={goCheckout}
                className="w-full rounded-full bg-cocoa py-6 text-base text-white hover:bg-cocoadark"
              >
                Checkout · Pickup Only
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
